import React, { useContext, useState } from "react"
import CertificationPackageBanner from "./CertificationPackageBanner"
import CertificationPackageCompanyInformation from "./CertificationPackageCompanyInformation"
import CertificationPackageInformation from "./CertificationPackageInformation"
import { Accordion, AccordionBody, AccordionHeading, AccordionItem, AccordionTitle, ButtonGroup, ButtonText, Col, Grid, Row, Typography } from "@visa/vds"
import { useForm } from "react-hook-form"
import CertificationPackageCompanyNotes from "./CertificationPackageCompanyNotes"
import CertificationPackageCompanyRequiredDocuments from "./CertificationPackageCompanyRequiredDocuments"
import { trackPromise } from "react-promise-tracker"
import { ceritificationPackageService } from "services/certification.certificationPackage"
import { AuthContext } from "context/AuthContext"
import "./CertificationPackage.css"
import { useHistory } from "react-router-dom/cjs/react-router-dom.min"
import CertificationPackageDialog from "./CertificationPackageDialog"
import { GlobalAlertContext } from "context/GlobalAlertContext"
import { CertificationPackageDetails, CertificationWorkflowPackagePermissions } from "_constants/CertificationWorkflowPackageStateConstants"
import MCSHeader from "components/MCSHeader"
import CertificationPackageCompletedInformation from "./CertificationPackageCompletedInformation"
import { IMPLEMENTATION_REQUIREMENTS } from "_constants/CertificationTestingConstants"

const CertificationPackageComponent = ({ loadedPackage, loadedConstants }) => {
    const userData = useContext(AuthContext).user;
    const alertContext = useContext(GlobalAlertContext);
    const history = useHistory()

    const [certificationPackage, setCertificationPackage] = useState(loadedPackage);
    const [originalRequiredDocuments, setOriginalRequiredDocuments] = useState(loadedPackage.certificationPackagePageRequiredDocuments)
    let packageControl = CertificationWorkflowPackagePermissions(userData, certificationPackage) //

    const { hasVisaSecureProductProviderAgreement } = certificationPackage.certificationPackageInformation
    const { control, handleSubmit, getValues, register, reset, setValue, watch } = useForm({
        shouldUnregister: false,
        defaultValues: {
            ...certificationPackage.certificationPackageInformation,
            ...certificationPackage.certificationPackagePageNotes,
            certificationPackagePageRequiredDocuments: certificationPackage.certificationPackagePageRequiredDocuments
        }
    });

    const responseWithUpdatedHelper = (updatedCertificationPackage) => {
        setCertificationPackage(updatedCertificationPackage)
        setOriginalRequiredDocuments(updatedCertificationPackage.certificationPackagePageRequiredDocuments)
        reset({
            ...updatedCertificationPackage.certificationPackageInformation,
            ...updatedCertificationPackage.certificationPackagePageNotes,
            certificationPackagePageRequiredDocuments: updatedCertificationPackage.certificationPackagePageRequiredDocuments
        })
        packageControl = CertificationWorkflowPackagePermissions(userData, updatedCertificationPackage)
    }

    const onSaveHelper = (apiCall, successMessage = "Successfully saved package") => {
        trackPromise(
            apiCall()
                .then((response) => {
                    console.log(response.data)
                    responseWithUpdatedHelper(response.data)
                    alertContext.setSuccessMessage(successMessage)
                })
                .catch((error) => alertContext.setErrorMessage(error?.response?.data ?? "Error Saving Package")))
            .finally(() => setOpenActionsDialog(false))
    }

    const onSave = () => {
        let updateDto = {
            ...getValues(),
            id: certificationPackage.certificationPackagePageBanner.id
        }
        onSaveHelper(() => ceritificationPackageService.UpdateCertificationPackage(updateDto))
    }

    const onSaveDocumentsOnly = () => {
        const packageState = getValues()
        let updateDto = {
            implementationTypeId: packageState.implementationTypeId,
            clientNotes: packageState.clientNotes,
            certificationPackagePageRequiredDocuments: packageState.certificationPackagePageRequiredDocuments,
            id: certificationPackage.certificationPackagePageBanner.id
        }
        onSaveHelper(() => ceritificationPackageService.UpdateCertificationPackageDocumentsOnly(updateDto))
    }

    const onRequestReview = () => {
        const packageState = getValues()
        let updateDto = {
            implementationTypeId: packageState.implementationTypeId,
            certificationPackagePageRequiredDocuments: packageState.certificationPackagePageRequiredDocuments,
            id: certificationPackage.certificationPackagePageBanner.id,
            clientNotes: packageState.clientNotes,
        }
        onSaveHelper(() => ceritificationPackageService.RequestReviewCertificationPackage(updateDto), "Thank you. Your file(s) has been submitted for review by Visa. If all files have been submitted, you will be informed of the outcome soon.")
    }

    const onSubmit = () => {
        let updateDto = {
            ...getValues(),
            id: certificationPackage.certificationPackagePageBanner.id
        }
        trackPromise(
            ceritificationPackageService.SubmitCertificationPackage(certificationPackage.certificationPackagePageBanner.id, updateDto)
                .then((response) => {
                    let updatedCertificationPackage = response.data
                    responseWithUpdatedHelper(updatedCertificationPackage)
                    if (updatedCertificationPackage.isSuccess) {
                        alertContext.setSuccessMessage(`Thank you. Your certification package has been submitted for review by Visa and you will be informed of the outcome soon.${updatedCertificationPackage.hasVstsSubscription
                            ? "" : "Please submit a VSTS Subscription order in the VSS Store."}`)
                    } else {
                        alertContext.setErrorMessage("Package updated but not submitted. Certification package fields are invalid")
                    }
                })
                .catch((error) => alertContext.setErrorMessage(error.response.data ?? "Error Submitting Package")))
            .finally(() => setOpenActionsDialog(false))
    }


    const onReturnToClient = () => {
        let updateDto = {
            ...getValues(),
            id: certificationPackage.certificationPackagePageBanner.id
        }
        onSaveHelper(() => ceritificationPackageService.ReturnToClientCertificationPackage(updateDto), "Package has been returned. Client will be informed of the returned status")
    }

    const onReject = () => {
        let updateDto = {
            ...getValues(),
            id: certificationPackage.certificationPackagePageBanner.id
        }
        onSaveHelper(() => ceritificationPackageService.RejectCertificationPackage(updateDto), "Package has been rejected. Client will be informed of the rejected status")
    }

    const onApprove = () => {
        let updateDto = {
            ...getValues(),
            id: certificationPackage.certificationPackagePageBanner.id
        }
        onSaveHelper(() => ceritificationPackageService.ApproveCertificationPackage(updateDto), "Package reviewed successfully. Client will be informed of reviewed status")
    }

    const onApproveDeferred = () => {
        const packageStateValues = getValues()
        let updateDto = {
            bid: certificationPackage.certificationPackagePageBanner.bid,
            notesToClient: packageStateValues.notesToClient,
            certificationPackagePageRequiredDocuments: packageStateValues.certificationPackagePageRequiredDocuments,
            id: certificationPackage.certificationPackagePageBanner.id,
        }
        onSaveHelper(() => ceritificationPackageService.ApproveDeferredCertificationPackage(updateDto), "Package reviewed successfully. Client will be informed of reviewed status.")
    }

    const fileRequiredAndMissingCheck = (document) => {
        return document.requirement !== IMPLEMENTATION_REQUIREMENTS.VISA_SECURE_PRODUCT_PROVIDER && !document.hasFile // document is not VSPPA and does not have file
            || document.requirement === IMPLEMENTATION_REQUIREMENTS.VISA_SECURE_PRODUCT_PROVIDER && !hasVisaSecureProductProviderAgreement && !document.hasFile  // document is VSPPA, does not have previous record, and does not have file
    }

    const handleChange = () => {

    }

    const onCancel = () => {
        trackPromise(
            ceritificationPackageService.CancelCertificationPackage(certificationPackage.certificationPackagePageBanner.id)
                .then((response) => {
                    if (response.status === 200) {
                        history.goBack()
                        alertContext.setSuccessMessage("Successfully cancelled package")
                    } else {
                        alertContext.setErrorMessage("Error cancelling package")
                    }
                })
                .catch(() => alertContext.setErrorMessage("Error cancelling package")))
    }


    const actionsDialogMapping = {
        Cancel: {
            dialogBody: "Are you sure you want to cancel this package? After cancellation, this package can no longer be edited.",
            dialogTitle: "Cancel Package",
            action: onCancel
        },
        SubmitWithWarning: {
            dialogBody: getValues().certificationPackagePageRequiredDocuments &&
                <>
                    <ul>
                        {getValues().certificationPackagePageRequiredDocuments.filter((filterDoc, filterIdx) => fileRequiredAndMissingCheck(filterDoc)).map((doc, idx) =>
                            <>
                                <li>{doc.requirementName}</li>
                            </>)
                        }
                    </ul>
                    Please note that this application is currently incomplete. In accordance with Visa’s policies, testing cannot commence,
                    and you cannot be issued a new compliance letter until all necessary requirements have been submitted and thoroughly reviewed.
                    We kindly ask you to return to the previous page and provide the outstanding requirements to facilitate a quicker testing and review process.
                </>,
            dialogTitle: "Warning - missing required documents",
            action: onSubmit
        },
        ApproveWithWarning: {
            dialogBody: getValues().certificationPackagePageRequiredDocuments &&
                <>
                    <Row>
                        <ul>
                            {getValues().certificationPackagePageRequiredDocuments.filter((filterDoc, filterIdx) => fileRequiredAndMissingCheck(filterDoc)).map((doc, idx) =>
                                <>
                                    <li>{doc.requirementName}</li>
                                </>)
                            }

                        </ul>
                    </Row>
                    <Row>
                        {`The package(s) will be ${packageControl.IsApprovedToTestingDeferred ? "Testing" : "Reviewed"} (Deferred) status if approved.`}
                    </Row>
                </>,
            dialogTitle: "Warning - missing required documents",
            action: onApprove,
            buttonText: "Continue to Approve"
        },
        Approve: {
            dialogBody: "",
            dialogTitle: "Confirm to Approve the package?",
            action: onApprove,
        },
        ApproveDeferred: {
            dialogBody: "",
            dialogTitle: "Confirm to Approve the package?",
            action: onApproveDeferred,
        },
        Reject: {
            dialogBody: "",
            dialogTitle: "Confirm to Reject the package?",
            action: onReject
        },
        Return: {
            dialogBody: "",
            dialogTitle: "Confirm to Return the package?",
            action: onReturnToClient,
        },
        RequestReview: {
            dialogBody: "",
            dialogTitle: "Confirm to Request for Review the documents?",
            action: onRequestReview,
        }
    }

    const [openActionsDialog, setOpenActionsDialog] = useState(false)
    const [actionsDialog, setActionsDialog] = useState(null)

    const handleActionButtonWithDialog = (action) => {
        setActionsDialog(actionsDialogMapping[action])
        setOpenActionsDialog(true);
    }

    const hasMissingFile = () => {
        var documents = getValues().certificationPackagePageRequiredDocuments
        return documents.find(document => fileRequiredAndMissingCheck(document)) != undefined;
    }

    return (
        <>
            {openActionsDialog && <CertificationPackageDialog title={actionsDialog.dialogTitle}
                                                              dialogType="warning" open={openActionsDialog} setOpen={setOpenActionsDialog}
                                                              leftButton={{ color: "secondary", text: actionsDialog.buttonText ?? "Confirm", handleClick: actionsDialog.action }}
                                                              rightButton={{ color: "primary", text: "Close" }}
            >
                {actionsDialog.dialogBody}
            </CertificationPackageDialog >
            }
            <MCSHeader header={`${packageControl.IsEditable ? "Update" : "View"} Certification Package`} />
            {
                certificationPackage ?

                    <Row >
                        <Col>
                            <CertificationPackageBanner isAdmin={packageControl.IsUserAdmin || packageControl.IsVisaStaffWithExt} banner={certificationPackage.certificationPackagePageBanner} />
                            <Accordion id="accordion-test-id" defaultExpandedId={[0]}>
                                <AccordionItem>
                                    <AccordionHeading>
                                        <AccordionTitle className="certification-package-details-accordion-title">Company Information</AccordionTitle>
                                    </AccordionHeading>
                                    <AccordionBody>
                                        <CertificationPackageCompanyInformation isEditable={packageControl.IsCompanyContactsEditable} packageBid={certificationPackage.certificationPackagePageBanner.bid} />
                                    </AccordionBody>
                                </AccordionItem>
                            </Accordion>
                            <Accordion id="accordion-test-id" defaultExpandedId={[0]}>
                                <AccordionItem>
                                    <AccordionHeading>
                                        <AccordionTitle className="certification-package-details-accordion-title">Package Information</AccordionTitle>
                                    </AccordionHeading>
                                    <AccordionBody>
                                        <CertificationPackageInformation
                                            control={control} isReadOnly={!packageControl.IsEditable} getValues={getValues} handleChange={handleChange} register={register}
                                            originalRequiredDocuments={originalRequiredDocuments}
                                            watch={watch} reset={reset}
                                            originalImplementationTypeId={certificationPackage.certificationPackageInformation.implementationTypeId}
                                            loadedConstants={loadedConstants}
                                        />

                                        {getValues().implementationTypeId &&
                                            <CertificationPackageCompanyRequiredDocuments
                                                control={control} reset={reset} getValues={getValues}
                                                packageId={certificationPackage.certificationPackagePageBanner.id}
                                                packageBid={certificationPackage.certificationPackagePageBanner.bid}
                                                isReadOnly={!packageControl.IsDocumentEditable}
                                                hasVisaSecureProductProviderAgreement={hasVisaSecureProductProviderAgreement}
                                            />
                                        }

                                        <CertificationPackageCompanyNotes
                                            control={control} handleChange={handleChange} register={register}
                                            packageId={certificationPackage.certificationPackagePageBanner.id}
                                            IsClientNotesEditable={packageControl.IsClientNotesEditable}
                                            IsInternalNotesViewable={packageControl.IsInternalNotesViewable}
                                            IsInternalNotesEditable={packageControl.IsInternalNotesEditable}
                                            IsNotesToClientViewable={packageControl.IsNotesToClientViewable}
                                            IsScreenshotsUploadable={packageControl.IsScreenshotsUploadable}
                                            IsScreenshotsViewable={packageControl.IsScreenshotsViewable}
                                        />
                                    </AccordionBody>
                                </AccordionItem>
                            </Accordion>
                            {
                                packageControl.IsCompleted &&
                                <Accordion id="accordion-test-id" defaultExpandedId={[0]}>
                                    <AccordionItem>
                                        <AccordionHeading>
                                            <AccordionTitle className="certification-package-details-accordion-title">Compliance Letter and Testing Information</AccordionTitle>
                                        </AccordionHeading>
                                        <AccordionBody>
                                            <CertificationPackageCompletedInformation bid={certificationPackage.certificationPackagePageBanner.bid} packageId={certificationPackage.certificationPackagePageBanner.id}
                                                                                      certificationPackageCompletedInformation={certificationPackage.certificationPackageCompletedInformation} />
                                        </AccordionBody>
                                    </AccordionItem>
                                </Accordion>
                            }
                            <Row>
                                <Col>
                                    <ButtonGroup direction="right">
                                        <ButtonText colorScheme="tertiary" onClick={() => history.goBack()}>Back</ButtonText>
                                        {packageControl.IsCancelButtonViewable && <ButtonText colorScheme="secondary" onClick={() => handleActionButtonWithDialog(CertificationPackageDetails.Cancel)}>Cancel Package</ButtonText>}
                                        {packageControl.IsReturnToClientButtonViewable && <ButtonText colorScheme="secondary" onClick={handleSubmit(() => handleActionButtonWithDialog(CertificationPackageDetails.Return))} >Return to Client</ButtonText>}
                                        {packageControl.IsDocumentEditable && <ButtonText colorScheme="secondary" onClick={packageControl.IsPackageStateDeferred ? handleSubmit(onSaveDocumentsOnly) : handleSubmit(onSave)}>Save</ButtonText>}
                                        {packageControl.IsRejectButtonViewable && <ButtonText colorScheme="primary" onClick={handleSubmit(() => handleActionButtonWithDialog(CertificationPackageDetails.Reject))}>Reject</ButtonText>}
                                        {(packageControl.IsApproveButtonViewable
                                                || (packageControl.IsPackageStateDeferred && packageControl.IsUserAdmin && getValues().certificationPackagePageRequiredDocuments.find(document => fileRequiredAndMissingCheck(document)) == null))
                                            && <ButtonText colorScheme="primary" onClick={handleSubmit(() => handleActionButtonWithDialog(
                                                packageControl.IsPackageStateDeferred ? CertificationPackageDetails.ApproveDeferred
                                                    : hasMissingFile() ? CertificationPackageDetails.ApproveWithWarning
                                                        : CertificationPackageDetails.Approve))}>
                                                {packageControl.IsPackageStateDeferred ? "Approve (Defer)" : "Approve"}

                                            </ButtonText>}
                                        {packageControl.IsSubmittable && <ButtonText colorScheme="primary" type="submit" onClick={
                                            hasMissingFile()
                                                ? handleSubmit(() => handleActionButtonWithDialog(CertificationPackageDetails.SubmitWithWarning))
                                                : handleSubmit(onSubmit)} >Submit</ButtonText>}
                                        {packageControl.IsRequestReviewButtonViewable
                                            && getValues().certificationPackagePageRequiredDocuments.find(document => document.hasFile) != null
                                            && <ButtonText colorScheme="primary" onClick={handleSubmit(() => handleActionButtonWithDialog(CertificationPackageDetails.RequestReview))}>Request Review</ButtonText>}
                                    </ButtonGroup>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    :
                    <Grid>
                        <Typography>Certification Package Not Found</Typography>
                    </Grid>
            }
        </>
    )
}

export default CertificationPackageComponent